import {RefObject, useLayoutEffect, useState} from 'react';

/**
 * Measures the column widths the automatic table layout produces and returns
 * them so that they can be applied through a `<colgroup>` with a fixed table
 * layout while the table body is virtualized. The browser then keeps those
 * widths instead of deriving them again from the content of the mounted rows,
 * which would let the columns change width whenever scrolling swaps the rows.
 *
 * Measuring must only happen while no widths are applied — a measurement of
 * the already fixed layout would just reproduce the applied widths and, with
 * rounding, slowly grow them. The widths are therefore stored together with
 * the column set they belong to, and the measuring effect does nothing as long
 * as widths are returned.
 *
 * The widths are measured again when the column set changes, when the width of
 * the scroll wrapper changes, and when virtualization starts. They are not
 * measured again on scrolling, sorting or loading further pages, so they
 * describe the rows of the first measured window and stay in place when the
 * caller replaces the data of a mounted table.
 *
 * Returns the widths in px by column, in the order of `columnIds`, or
 * `undefined` while the automatic layout applies.
 */
export function useStableColumnWidths({
  tableRef,
  wrapperRef,
  columnIds,
  enabled,
}: {
  tableRef: RefObject<HTMLTableElement | null>;
  /** The scrollable element that wraps the table. */
  wrapperRef: RefObject<HTMLDivElement | null>;
  /** Ids of the visible leaf columns, in render order. */
  columnIds: string[];
  enabled: boolean;
}): number[] | undefined {
  const columnsKey = columnIds.join();
  const [measured, setMeasured] = useState<{
    columnsKey: string;
    widths: number[];
  }>();

  // a changed column set makes the measured widths obsolete without a render
  // in between, so no frame applies them to the wrong columns
  const widths =
    enabled && measured?.columnsKey === columnsKey
      ? measured.widths
      : undefined;

  useLayoutEffect(() => {
    if (!enabled) {
      setMeasured(undefined);
      return;
    }
    if (widths) {
      return;
    }
    const table = tableRef.current;
    const headerRow = table?.querySelector(':scope > thead > tr:last-of-type');
    if (!table || !headerRow) {
      return;
    }
    const headerCells = [...headerRow.children];
    if (headerCells.length !== columnIds.length) {
      // a header cell spans several columns: keep the automatic layout
      return;
    }
    const tableWidth = table.getBoundingClientRect().width;
    if (tableWidth === 0) {
      // not laid out yet; measuring runs again once a width is available
      return;
    }
    const cellWidths = headerCells.map(
      (cell) => cell.getBoundingClientRect().width
    );
    // the last column takes the remainder, so the widths add up to exactly the
    // table width and the fixed layout leaves neither a gap nor an overflow
    cellWidths[cellWidths.length - 1] =
      tableWidth -
      cellWidths.slice(0, -1).reduce((total, width) => total + width, 0);
    setMeasured({columnsKey, widths: cellWidths});
  }, [enabled, widths, columnsKey, columnIds.length, tableRef]);

  // the widths of the automatic layout depend on the available space
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!enabled || !wrapper) {
      return;
    }
    // the client width ignores a scrollbar the fixed layout may add, which
    // would otherwise trigger the observer through its own effect
    let previousWidth = wrapper.clientWidth;
    const observer = new ResizeObserver(() => {
      if (wrapper.clientWidth !== previousWidth) {
        previousWidth = wrapper.clientWidth;
        setMeasured(undefined);
      }
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [enabled, wrapperRef]);

  return widths;
}
